// UserService.js
import { API, apiRequest } from './api';
import { ApiError } from './errors';

export async function loginUser(email, password) {
  try {
    const response = await apiRequest(API.LOGIN, {
      method: 'POST',
      body: { email, password },
    });
    return response.json();
  } catch (e) {
    if (e?.name === 'ApiError') throw new ApiError('login', e.status);
    throw e;
  }
}

export async function register({ email, name, password }) {
  try {
    const response = await apiRequest(API.REGISTER, {
      method: 'POST',
      body: { email, name, password },
    });
    return response.json();
  } catch (e) {
    if (e?.name === 'ApiError') throw new ApiError('register', e.status);
    throw e;
  }
}

export async function getUserProfile() {
  // Use the existing /me endpoint defined in API.
  try {
    const token = localStorage.getItem('token');
    const response = await apiRequest(API.USERS_ME, {
      method: 'GET',
      token,
    });
    return await response.json();
  } catch (e) {
    console.error('Error fetching user profile:', e);
    if (e?.name === 'ApiError') throw new ApiError('access', e.status);
    throw e;
  }
}

export async function updateUserProfile(profileData) {
  // Backwards compat: map to update-account endpoint.
  // Prefer `updateAccount(...)` elsewhere.
  try {
    const token = localStorage.getItem('token');
    const response = await apiRequest(API.UPDATE_ACCOUNT, {
      method: 'PUT',
      body: profileData,
      token,
    });
    return await response.json();
  } catch (e) {
    console.error('Error updating user profile:', e);
    if (e?.name === 'ApiError') throw new ApiError('access', e.status);
    throw e;
  }
}

export async function deleteUser() {
  // Use /me semantics: backend returns 204 for delete current user.
  try {
    const token = localStorage.getItem('token');
    const response = await apiRequest(API.USERS_ME, {
      method: 'DELETE',
      token,
    });
    // 204 has no content.
    if (response.status === 204) return null;
    const contentType = response.headers.get('Content-Type') || '';
    if (contentType.includes('application/json')) return await response.json();
    return await response.text();
  } catch (e) {
    console.error('Error deleting user:', e);
    if (e?.name === 'ApiError') throw new ApiError('access', e.status);
    throw e;
  }
}

export async function requestPasswordReset(email, recaptchaToken) {
  try {
    const response = await apiRequest(API.REQUEST_RESET_PASSWORD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { email, recaptchaToken },
    });
    return response.text(); // plain text OK
  } catch (e) {
    if (e?.name === 'ApiError')
      throw new ApiError('requestResetPassword', e.status);
    throw e;
  }
}

export async function resetPassword(token, newPassword) {
  try {
    const response = await apiRequest(API.RESET_PASSWORD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { token, newPassword },
    });
    return response.text();
  } catch (e) {
    if (e?.name === 'ApiError') throw new ApiError('resetPassword', e.status);
    throw e;
  }
}

export async function changePassword(currentPassword, newPassword) {
  try {
    const token = localStorage.getItem('token');
    const response = await apiRequest(API.CHANGE_PASSWORD, {
      method: 'POST',
      token,
      body: { currentPassword, newPassword },
    });
    return await response.json();
  } catch (e) {
    console.error('Error changing password:', e);
    if (e?.name === 'ApiError') throw new ApiError('changePassword', e.status);
    throw e;
  }
}

export async function getUserByEmail(email) {
  try {
    const response = await apiRequest(`${API.ADMIN_USERS}/${email}`, {
      method: 'GET',
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching user by email:', error);
    if (error?.name === 'ApiError') throw new ApiError('access', error.status);
    throw error;
  }
}

export async function getPurchasesByUserEmail(email) {
  try {
    const response = await apiRequest(`${API.ADMIN_USERS}/${email}/purchases`, {
      method: 'GET',
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching purchases by user email:', error);
    if (error?.name === 'ApiError') throw new ApiError('access', error.status);
    throw error;
  }
}

export async function updateAccount(
  { currentPassword, newPassword, newEmail, newName },
  token,
) {
  try {
    const response = await apiRequest(API.UPDATE_ACCOUNT, {
      method: 'PUT',
      body: { currentPassword, newPassword, newEmail, newName },
      token,
    });
    // apiRequest throws on non-2xx; if here, parse JSON response
    return await response.json();
  } catch (error) {
    console.error('Error updating account:', error);
    if (error?.name === 'ApiError') throw new ApiError('access', error.status);
    throw error;
  }
}

// Passwordless register: creates/updates user and returns a short-lived JWT
export async function passwordlessRegister({ email, name, age }) {
  if (!email || !name) throw new Error('Email and name are required');
  try {
    const response = await apiRequest(API.PASSWORDLESS_REGISTER, {
      method: 'POST',
      body: { email, name, age },
    });
    return response.json();
  } catch (e) {
    if (e?.name === 'ApiError') throw new ApiError('register', e.status);
    throw e;
  }
}
